import { GRAMMAR_RULES } from './GrammarRules';

/**
 * Base Strategy for Autocomplete
 * Contains shared logic for tokenization and common data processing
 */
export class BaseStrategy {
    constructor(keywordsData, monaco, version) {
        this.version = version || "MA2";
        this.keywordsData = keywordsData;
        this.keywordsDict = keywordsData?.keywords || {};
        this.monaco = monaco;
        this.grammar = GRAMMAR_RULES[this.version] || GRAMMAR_RULES.MA2;
        this.TRIGGER_NEXT = { id: "editor.action.triggerSuggest", title: "Trigger Suggest" };
        this.VAR_KEYWORDS = ["SetVar", "SetUserVar", "AddVar", "AddUserVar", "GetVar", "GetUserVar", "SetUserVariable", "GetUserVariable"];
        this.HELPERS = ["At", "If", "Thru", "+", "-", "Wait"];
    }

    /**
     * Common tokenization logic
     */
    analyzeContext(model, position) {
        const lineContent = model.getLineContent(position.lineNumber);
        const textUntilPosition = lineContent.substring(0, position.column - 1);
        const wordsRaw = textUntilPosition.split(/\s+/);
        const words = wordsRaw.filter(w => w.length > 0);
        const wordInfo = model.getWordUntilPosition(position);

        let currentWord = (wordInfo.word || "").toLowerCase();
        let startColumn = wordInfo.startColumn;

        // Monaco's wordInfo usually stops at / or $
        // We look exactly at the character before the word starts (column is 1-indexed)
        const charIndexBefore = startColumn - 2;
        const charBefore = charIndexBefore >= 0 ? lineContent[charIndexBefore] : null;

        if (charBefore === '/' || charBefore === '$') {
            startColumn -= 1;
            currentWord = charBefore + currentWord;
        }
        // Or if we just typed the trigger itself
        else {
            const lastChar = textUntilPosition.slice(-1);
            if (lastChar === '/' || lastChar === '$') {
                startColumn = position.column - 1;
                currentWord = lastChar;
            }
        }

        const isAtSpace = textUntilPosition.endsWith(' ');
        const lastWord = words.length > 0 ? (isAtSpace ? words[words.length - 1] : words[words.length - 2]) : null;

        const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: startColumn,
            endColumn: wordInfo.endColumn
        };

        return {
            textUntilPosition,
            words,
            wordsRaw,
            currentWord,
            isAtSpace,
            lastWord,
            range
        };
    }

    /**
     * Extracts all variable names (starting with $) from the current document
     */
    getDocumentVariables(model) {
        const text = model.getValue();
        const varRegex = /\$[a-zA-Z0-9_]+/g;
        const matches = text.match(varRegex) || [];
        return [...new Set(matches)];
    }

    /**
     * Basic Variable Suggestions
     */
    getVariableSuggestions(context, model) {
        const { textUntilPosition, wordsRaw, range } = context;
        const suggestions = [];

        const currentFullWord = wordsRaw[wordsRaw.length - 1] || "";

        if (currentFullWord.includes('$')) {
            const dollarIndex = currentFullWord.lastIndexOf('$');
            const varPrefix = currentFullWord.substring(dollarIndex);

            if (varPrefix.startsWith('$')) {
                const existingVars = this.getDocumentVariables(model);
                existingVars.forEach(v => {
                    if (v.toLowerCase().startsWith(varPrefix.toLowerCase()) && v !== varPrefix) {
                        suggestions.push({
                            label: v,
                            kind: this.monaco.languages.CompletionItemKind.Variable,
                            insertText: v.substring(1) + " ",
                            range: range,
                            detail: "Existing Variable",
                            command: this.TRIGGER_NEXT
                        });
                    }
                });
            }
        }
        return suggestions;
    }

    /**
     * Basic Option Suggestions (starting with /)
     */
    getOptionSuggestions(context) {
        const { words, currentWord, range } = context;
        if (!currentWord.startsWith('/')) return [];

        const suggestions = [];
        const commandWord = (words[0] || "").toLowerCase();
        const kwName = Object.keys(this.keywordsDict).find(k => k.toLowerCase() === commandWord);
        const commandData = kwName ? this.keywordsDict[kwName] : null;

        if (commandData && commandData.options) {
            const searchStr = currentWord.toLowerCase();

            commandData.options.forEach(opt => {
                if (!opt || !opt.option) return;
                const optName = opt.option.startsWith('/') ? opt.option : '/' + opt.option;

                if (optName.toLowerCase().startsWith(searchStr) || searchStr === '/') {
                    suggestions.push({
                        label: optName,
                        kind: this.monaco.languages.CompletionItemKind.Property,
                        insertText: optName + " ",
                        range: range,
                        command: this.TRIGGER_NEXT,
                        documentation: {
                            value: `**${optName}**\n\n${opt.description || ""}${opt.shortcut ? `\n\nShortcut: \`${opt.shortcut}\`` : ""}`
                        },
                        detail: "Option"
                    });
                }
            });
        }
        return suggestions;
    }

    /**
     * Finds the best matching syntax model for the current words
     */
    findBestSyntaxMatch(commandWord, words) {
        if (!commandWord) return null;
        const kwName = Object.keys(this.keywordsDict).find(k => k.toLowerCase() === commandWord.toLowerCase());
        const commandData = kwName ? this.keywordsDict[kwName] : null;

        if (!commandData || !commandData.syntax_models || commandData.syntax_models.length === 0) {
            return null;
        }

        // Try to find the model that matches the most words
        let bestMatch = null;
        let maxMatches = -1;

        commandData.syntax_models.forEach(model => {
            let matchCount = 0;
            for (let i = 0; i < Math.min(words.length, model.length); i++) {
                const token = model[i];
                const word = words[i].toLowerCase();

                if (token.type === "keyword" && token.value.toLowerCase() === word) {
                    matchCount++;
                } else if (token.type === "object" || token.type === "number") {
                    // Primitive check: if it's not a keyword, assume it matches a placeholder for now
                    // In a real IDE, we would validate if words[i] is a valid object or number
                    matchCount++;
                }
            }

            if (matchCount > maxMatches) {
                maxMatches = matchCount;
                bestMatch = model;
            }
        });

        return bestMatch;
    }

    /**
     * Gets suggestions based on the current position in the syntax model
     */
    getSyntaxSuggestions(context) {
        const { words, isAtSpace, currentWord, range } = context;
        if (words.length === 0) return [];

        const commandWord = words[0];
        const bestModel = this.findBestSyntaxMatch(commandWord, words);
        if (!bestModel) return [];

        const suggestions = [];

        // Determine which token index we are currently at
        // If we are at a space, we are looking for the NEXT token
        // If we are typing a word, we are looking for the CURRENT token
        const currentTokenIndex = isAtSpace ? words.length : words.length - 1;

        if (currentTokenIndex < bestModel.length) {
            const nextToken = bestModel[currentTokenIndex];

            if (nextToken.type === "keyword") {
                if (nextToken.value.toLowerCase().startsWith(currentWord)) {
                    suggestions.push({
                        label: nextToken.value,
                        kind: this.monaco.languages.CompletionItemKind.Keyword,
                        insertText: nextToken.value + " ",
                        range: range,
                        command: this.TRIGGER_NEXT,
                        detail: nextToken.required ? "Required" : "Optional"
                    });
                }
            } else if (nextToken.type === "object" || nextToken.type === "number") {
                // Resolve placeholder using dynamic registry lookup or fallback to grammar
                let possibleObjects = this.getKeywordsByType(nextToken.type);

                // If no dynamic matches, fallback to GrammarRules (for overlaps/aliases)
                if (possibleObjects.length === 0) {
                    possibleObjects = this.grammar.placeholders[nextToken.label] || [];
                }

                possibleObjects.forEach(obj => {
                    const label = typeof obj === 'string' ? obj : obj.label;
                    if (label.toLowerCase().startsWith(currentWord)) {
                        suggestions.push({
                            label: label,
                            kind: this.monaco.languages.CompletionItemKind.Class,
                            insertText: label + " ",
                            range: range,
                            command: this.TRIGGER_NEXT,
                            detail: `Syntax: [${nextToken.label}]`
                        });
                    }
                });

                // Show the hint if still no suggestions or it's a number
                if (suggestions.length === 0 || nextToken.type === "number") {
                    suggestions.push({
                        label: `[${nextToken.label}]`,
                        kind: this.monaco.languages.CompletionItemKind.Snippet,
                        insertText: "",
                        range: range,
                        detail: `Expected: ${nextToken.type}`
                    });
                }
            }
        }

        return suggestions;
    }

    /**
     * Finds all keywords in the registry with a specific semantic type
     */
    getKeywordsByType(type) {
        return Object.entries(this.keywordsDict)
            .filter(([name, data]) => data.semantic_type === type)
            .map(([name]) => name);
    }
}
